import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import getCookie from "../../utilities/getCookie"
import { decodeToken } from "react-jwt"
import { PrimeButton } from "components-design-system"
import { REFRESH_TOKEN_KEY } from "constants/appState.js"
import useRefreshToken from "utilities/useRefreshToken"
import SessionCountdown from "components/SessionCountdown/SessionCountdown"
import APP_STATE from "constants/appState.js"
import useAppState from "../../context/hooks/useAppState"
import { useGetEnvVars } from "api/hooks/enum"
import "./useTokenExpirationNotifier.scss"

const useTokenExpirationNotifier = () => {
  const navigate = useNavigate()
  const { data: envVars } = useGetEnvVars()
  const { openToastNotification, appState } = useAppState()
  const { refreshTokens, isRefreshTokenExpired, onSessionExpired, setNextUrlLocalStorage } = useRefreshToken()
  const notifApiRef = useRef(null)

  const notifTriggeredRef = useRef(false)
  const firstNotifTriggeredRef = useRef(false)
  const secondNotifTriggeredRef = useRef(false)
  const intervalIdRef = useRef(false)

  const resetNotifTriggers = () => {
    notifTriggeredRef.current = false
    firstNotifTriggeredRef.current = false
    secondNotifTriggeredRef.current = false
  }

  useEffect(() => {
    const checkTokenExpiration = () => {
      const logoutUser = () => {
        setNextUrlLocalStorage()
        if (notifApiRef?.current) notifApiRef?.current?.destroy()
        onSessionExpired()
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
        resetNotifTriggers()
      }

      const expTime = decodeToken(getCookie(REFRESH_TOKEN_KEY))?.exp
      const isExpired = isRefreshTokenExpired()
      if (isExpired === true) {
        logoutUser()
        return
      }

      const exp = expTime * 1000
      const currentTime = new Date()
      const timeDifference = exp - currentTime

      const warningTime = Number(envVars?.REACT_APP_SESSION_TIMEOUT_WARNING_INTERVAL || "300000")
      if (isNaN(warningTime)) {
        warningTime = 300000
      }

      // Check if the token is about to expire in 5 minutes
      if (
        timeDifference > 0 &&
        timeDifference <= warningTime &&
        !notifTriggeredRef.current &&
        !firstNotifTriggeredRef.current
      ) {
        notifApiRef.current = triggerNotif()
        notifTriggeredRef.current = true
        firstNotifTriggeredRef.current = true
      }

      // Check if the token is about to expire in 1 minute
      else if (
        timeDifference > 0 &&
        timeDifference <= 1 * 60 * 1000 &&
        !notifTriggeredRef.current &&
        !secondNotifTriggeredRef.current
      ) {
        notifApiRef.current = triggerNotif()
        notifTriggeredRef.current = true
        secondNotifTriggeredRef.current = true
      }

      if (isRefreshTokenExpired()) {
        logoutUser()
        return
      }
    }

    const triggerNotif = () => {
      return openToastNotification(
        "warning",
        "Session is expiring soon",
        <div className="session-expiration-notif">
          <SessionCountdown expirationTime={decodeToken(getCookie(REFRESH_TOKEN_KEY)).exp} />
          <PrimeButton
            className="continue-working-btn"
            size="small"
            onClick={async () => {
              await refreshTokens()
              resetNotifTriggers()
              notifApiRef.current.destroy()
            }}
          >
            Extend session
          </PrimeButton>
        </div>,
        null,
        {
          duration: null,
          onClose: () => (notifTriggeredRef.current = false),
        }
      )
    }

    if (intervalIdRef.current === null && appState === APP_STATE.authenticated) {
      const interval = setInterval(checkTokenExpiration, 3000)
      intervalIdRef.current = interval
    }

    if (appState === APP_STATE.unauthenticated && notifApiRef?.current) {
      notifApiRef?.current?.destroy()
    }

    return () => {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
  }, [appState])
}

export default useTokenExpirationNotifier
